import React, { FunctionComponent, ReactNode } from 'react'
import { Heading, Text, Grid, Message, Divider, Button, Link } from '@afterpaytouch/core'
import { Trans, useTranslation } from 'next-i18next'
import { useConsumerSizes } from '../../../utils/responsive'
import { Head, PulseBenefitsList } from '../..'
import { useAmplitudeWithEnduringEventProperties } from '../../../integrations/amplitude'
import { useGetConsumerLoyaltyStatusQuery } from '../../../state'
import { TrackingEvent } from '../../../model/amplitude'
import { LogOnMount } from 'react-amplitude-hooks'

const I18N_NAMESPACE = ['common', 'account', 'loyalty']

export interface DetailRowProps {
  label: string
  value: ReactNode
  isEditable?: boolean
  onClick?: () => void
}

const DetailRow: FunctionComponent<DetailRowProps> = ({ label, value, isEditable = false, onClick = () => {} }: DetailRowProps) => {
  const { t } = useTranslation(I18N_NAMESPACE)

  return (
    <div className='mb-8'>
      <div className='flex flex-row justify-between'>
        <div>
          <Text size='S' color='Gray40'>
            {label}
          </Text>
        </div>
        {isEditable && (
          <Button.Link size='Regular' onClick={onClick} aria-label='edit'>
            {t('account:tab:profile:body:cta')}
          </Button.Link>
        )}
      </div>
      <div className='my-2'>
        <Text size='M'>{value}</Text>
      </div>
      <Divider kind='Hairline' />
    </div>
  )
}

export const PulseTab: FunctionComponent = () => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const { subheadingSize } = useConsumerSizes()
  const { logEvent } = useAmplitudeWithEnduringEventProperties()

  const pageTitle = `${t('account:tab:pulse:title')} - ${t('account:title')} | ${t('common:brandName')}`

  const onClickMobileAppPulseDeeplink = (): void => logEvent('Clicked Mobile App Pulse Deeplink')

  const testNameSpace = 'pulse-tab'

  const { data: consumerLoyaltyStatusData } = useGetConsumerLoyaltyStatusQuery()

  // @ts-ignore: OPERATION BLEED STOPPER
  const pulseTier = consumerLoyaltyStatusData?.tiers.find((tier) => tier.tierId === consumerLoyaltyStatusData.pulseStatus.currentTier)
  const pulseTierName = pulseTier?.tierName
  const pulsePoints = consumerLoyaltyStatusData?.cycleProgress?.currentPoints
  // @ts-ignore: OPERATION BLEED STOPPER
  const pulseTiers = consumerLoyaltyStatusData?.tiers?.slice(0, Number(consumerLoyaltyStatusData.pulseStatus.currentTier) + 1)
  const pulseBenefits = pulseTiers?.flatMap((tier) => tier.benefits).reverse()

  return (
    <>
      <LogOnMount eventType={TrackingEvent.VIEWED_PULSE_REWARDS_PAGE} />
      <Head title={pageTitle} />
      <Heading bold size={subheadingSize} testNameSpace={`${testNameSpace}-title`}>
        {t('account:tab:pulse:body:title')}
      </Heading>
      <div className='mt-8'>
        <Grid>
          <Grid.Item sm={12} lg={6}>
            <DetailRow label={t('account:tab:pulse:body:labelPoints')} value={pulsePoints} />
            <DetailRow label={t('account:tab:pulse:body:labelTier')} value={pulseTierName} />
            {/* @ts-ignore: OPERATION BLEED STOPPER */}
            <DetailRow label={t('account:tab:pulse:body:labelBenefits', { pulseTierName })} value={<PulseBenefitsList pulseBenefits={pulseBenefits} />} />
          </Grid.Item>
          <Grid.Item sm={12} lg={6}>
            <div className='lg:flex lg:flex-row lg:justify-center lg:px-8'>
              <div className='max-w-[368px]'>
                <Message
                  kind='Success'
                  iconName='Lock'
                  size='Regular'
                  heading={t('account:tab:pulse:body:message:title')}
                  description={
                    <Text size='XS'>
                      <Trans
                        i18nKey={'account:tab:pulse:body:message:description'}
                        components={{
                          mobileAppPulseDeeplink: (
                            <Link href={t('common:urls:openMobileApp')} kind='Underline' target='_blank' onClick={onClickMobileAppPulseDeeplink} />
                          ),
                        }}
                      />
                    </Text>
                  }
                />
              </div>
            </div>
          </Grid.Item>
        </Grid>
      </div>
    </>
  )
}
